import { getTypedSignatureName } from '@shared/components/signature/SignatureCapture';

import type {
  AgreementExecutionResponse,
  AgreementReviewResponse,
  AgreementSignRequest,
} from '../../types';

import type { AgreementPackageView } from './AgreementsSetup.types';

export function capturedSignature(value: string): AgreementSignRequest['signature'] {
  const name = getTypedSignatureName(value);
  if (name?.trim()) return { kind: 'typed', name, capture_value: value };
  if (value.startsWith('data:image/png;base64,')) return { kind: 'drawn_png', data_uri: value };
  throw new Error('Please draw or type your signature.');
}

export function agreementView(
  review: AgreementReviewResponse,
  executions: readonly AgreementExecutionResponse[],
): AgreementPackageView {
  return {
    id: review.package.package_key,
    title: review.package.package_kind === 'managed' ? 'Managed services' : 'Self-serve billing',
    description: 'Your prepared agreement package',
    services: review.package.selected_service_keys,
    documents: review.package.documents.map(document => {
      const execution = executions.find(
        item => item.envelope.document_key === document.document_key,
      );
      const signature = execution?.envelope.request.signature;
      return {
        id: document.document_key,
        title: document.title,
        versionLabel: document.version_label,
        sections: document.sections,
        orderForm: document.order_form.length
          ? document.order_form.map(field => ({ label: field.label, value: field.display_value }))
          : undefined,
        jotpsychSignature: {
          name: document.jotpsych_signature.name ?? 'JotPsych',
          required: document.jotpsych_signature.required,
          title: document.jotpsych_signature.title ?? '',
          imageDataUri: document.jotpsych_signature.image_data_uri ?? undefined,
        },
        acceptanceText: document.consent.text,
        authorityText: document.consent.authority_text,
        signed:
          execution && signature
            ? {
                signature:
                  signature.kind === 'typed' ? signature.capture_value : signature.data_uri,
                signerName: execution.envelope.signer.name ?? 'Practice administrator',
                signedAtLabel: new Date(execution.envelope.received_at).toLocaleString(),
              }
            : undefined,
      };
    }),
  };
}

export function agreementErrorMessage(error: unknown): string {
  const code =
    error &&
    typeof error === 'object' &&
    'data' in error &&
    error.data &&
    typeof error.data === 'object' &&
    'error_code' in error.data
      ? error.data.error_code
      : undefined;
  if (code === 'agreement_conflict')
    return 'This agreement changed or was already signed. Reload the saved agreement before continuing.';
  if (code === 'agreement_disabled') return 'Agreement signing is not enabled for this practice.';
  if (code === 'agreement_invalid') return 'Review your selections and signature, then try again.';
  if (code === 'agreement_too_large')
    return 'This agreement could not be saved. Please contact your JotPsych team.';
  return 'We could not confirm the saved agreement. Try again or reload before continuing.';
}
